import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { getFormattedErr } from 'src/helpers';
import apiClient from 'src/helpers/apiClient';
import type {
  AddItemToCartBody,
  CancelPaymentBody,
  CartCheckoutBody,
  FinishPaymentBody,
  PaymentApiGetAdminOrderInvoiceKindEnum,
  UpdateCartBody,
  UpsertShippingAddressBody,
  UserOfferResponseBody,
} from 'src/openapi';
import type { RootState } from '..';

/** Where a Pro offer window may be opened from (server: OfferMoment). */
export type OfferMoment =
  | 'apply'
  | 'return'
  | 'lesson_done'
  | 'resume_download'
  | 'multiplayer_done';

export const getStoreItems = createAsyncThunk(
  'store/getStoreItems',
  /** `openOffer`: a Pro surface is on screen, so a time-limited Pro offer may
   * open now (the server moves the buyer's clock only on such a read).
   * `offerMoment`: the moment it is shown at ('apply': back from applying),
   * for an offer that opens only there. */
  async (
    arg: { openOffer?: boolean; offerMoment?: OfferMoment } | undefined,
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetAllItemsPublic(
        { platform: 'web' },
        arg?.openOffer
          ? {
              params: {
                open_offer: true,
                ...(arg.offerMoment && { offer_moment: arg.offerMoment }),
              },
            }
          : undefined,
      );
      // A time-limited offer's end, on this device's clock: the server says
      // how many seconds are left, counted from now, so a phone whose clock
      // is off still shows the real countdown.
      const receivedAt = Date.now();
      const items = (res.data.items || []).map((item) => {
        const left = (item as { offer_seconds_left?: number | null })
          .offer_seconds_left;
        return left == null
          ? item
          : { ...item, offer_deadline_ms: receivedAt + left * 1000 };
      });
      // The envelope carries the Pro unlock counts too — the paywall quotes
      // them, so returning only `items` here would mean a second request for two
      // integers that already arrived.
      return {
        items,
        proUnlocks: res.data.pro_unlocks,
      };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchActiveOffers = createAsyncThunk(
  'store/fetchActiveOffers',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getCouponApi()
      ).couponApiGetActiveOffers();
      return res.data.offers || [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCartDetails = createAsyncThunk(
  'store/getCartDetails',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (await apiClient.getPaymentApi()).paymentApiGetCart();
      if (Object.keys(res.data).length === 0) return null;
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const addToCart = createAsyncThunk(
  'store/addToCart',
  async (
    {
      items,
      couponCode,
    }: {
      items?: AddItemToCartBody[];
      couponCode?: string | null;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiAddItemToCart({
        addItemToCartInBulk: {
          items,
          coupon_code: couponCode,
        },
      });
      if (res.data.success) {
        await dispatch(getCartDetails());
      }
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const removeFromCart = createAsyncThunk(
  'store/removeFromCart',
  async (
    { cartId }: { cartId: string },
    { dispatch, rejectWithValue, getState },
  ) => {
    try {
      const {
        store: { cartDetails },
      } = getState() as RootState;

      const cart = cartDetails?.cart;
      if (!cart) {
        return rejectWithValue('Please try after some time.');
      }

      const newCartItems = cart
        ?.filter((item) => item.id !== cartId)
        .map(
          (item) =>
            ({
              item_id: item.item_id,
              quantity: item.quantity,
              payment_mode: item.total_amount_in_currency ? 'Currency' : 'Gems',
            }) as UpdateCartBody,
        );

      if (newCartItems === undefined) {
        return rejectWithValue('Please try after some time.');
      }

      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiUpdateCartItems({ updateCartBody: newCartItems });

      if (res.data.success) {
        await dispatch(getCartDetails());
      }

      return res.data.success;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateCartItemQuantity = createAsyncThunk(
  'store/updateCartItemQuantity',
  async (
    { cartId, quantity }: { cartId: string; quantity: number },
    { dispatch, rejectWithValue, getState },
  ) => {
    try {
      const {
        store: { cartDetails },
      } = getState() as RootState;
      const cart = cartDetails?.cart;

      if (!cart) {
        return rejectWithValue('Please try after some time.');
      }

      const updatingItem = cart?.find((item) => item.id === cartId);
      if (!updatingItem) {
        return rejectWithValue('Selected item not found.');
      }

      const newCartItems = cart?.map((item) => {
        if (item.id === cartId) {
          return {
            item_id: item.item_id,
            quantity,
            payment_mode: item.total_amount_in_currency ? 'Currency' : 'Gems',
          } as UpdateCartBody;
        }
        return {
          item_id: item.item_id,
          quantity: item.quantity,
          payment_mode: item.total_amount_in_currency ? 'Currency' : 'Gems',
        } as UpdateCartBody;
      });

      if (newCartItems === undefined) {
        return rejectWithValue('Selected item not found.');
      }

      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiUpdateCartItems({ updateCartBody: newCartItems });

      if (res.data.success) {
        await dispatch(getCartDetails());
      }

      return res.data.success;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getPaymentAddress = createAsyncThunk(
  'store/getPaymentAddress',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetUserAddressDetails();
      if (res.data.billing || res.data.shipping) {
        return res.data;
      }
      return undefined;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updatePaymentAddress = createAsyncThunk(
  'store/updatePaymentAddress',
  async (
    {
      shippingAddress,
      billingAddress,
    }: {
      shippingAddress: UpsertShippingAddressBody;
      billingAddress: UpsertShippingAddressBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiUpsertUserAddressDetails({
        upsertAddressBody: {
          shipping: shippingAddress,
          billing: billingAddress,
        },
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const cartCheckout = createAsyncThunk(
  'store/cartCheckout',
  async (param: CartCheckoutBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiMakePayment({ cartCheckoutBody: param });
      return res.data;
    } catch (err: any) {
      const error = err?.response?.data?.error;
      enqueueSnackbar(
        {
          pro_offer_ended:
            'That offer has ended, so the price has changed. Check the new total before you pay.',
          price_changed:
            'The price has changed. Check the new total before you pay.',
        }[error as string] ?? getFormattedErr(error),
      );
      return rejectWithValue(err.response.data);
    }
  },
);

export const paymentFinish = createAsyncThunk(
  'store/paymentFinish',
  async (param: FinishPaymentBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiCompleteTransaction({ finishPaymentBody: param });
      if (response.data.success) {
        const status = response.data.transaction?.payment_status;
        if (status === 'partially_successful') {
          enqueueSnackbar(
            'Gems portion paid successfully. Currency payment failed.',
            { variant: 'warning', autoHideDuration: 5000 },
          );
        } else {
          enqueueSnackbar('Payment completed successfully', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        }
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const paymentCancel = createAsyncThunk(
  'store/paymentCancel',
  async (param: CancelPaymentBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiCancelTransaction({ cancelPaymentBody: param });
      if (response.data.success) {
        const isPartial =
          response.data.transaction?.payment_status === 'partially_successful';
        if (isPartial) {
          enqueueSnackbar(
            'Gems portion paid successfully. Currency payment was cancelled.',
            { variant: 'warning', autoHideDuration: 5000 },
          );
        } else {
          enqueueSnackbar('Payment cancelled successfully', {
            variant: 'info',
            autoHideDuration: 3000,
          });
        }
      }
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getOrderHistory = createAsyncThunk(
  'store/getOrderHistory',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiUserOrderHistory();

      const sortedOrders = res.data.sort(
        (a, b) =>
          new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime(),
      );
      const visibleOrders = sortedOrders.filter((order) =>
        ['succeeded', 'partially_successful'].includes(
          order.payment_status ?? '',
        ),
      );
      return visibleOrders;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getRareStoreItems = createAsyncThunk(
  'store/item/rare',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetAllItemsPublic({ showActiveOffers: true });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const claimOrderVoucher = createAsyncThunk(
  'store/claimOrderVoucher',
  async ({ transactionId }: { transactionId: string }, { rejectWithValue }) => {
    try {
      await (
        await apiClient.getPaymentApi()
      ).paymentApiClaimOrderVoucher({ transactionId });
      return transactionId;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const redeliverOrder = createAsyncThunk(
  'store/redeliverOrder',
  async ({ transactionId }: { transactionId: string }, { rejectWithValue }) => {
    try {
      await (
        await apiClient.getPaymentApi()
      ).paymentApiRedeliverOrder({ transactionId });
      enqueueSnackbar('Order re-delivered successfully', {
        variant: 'success',
      });
      return true;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

const INVOICE_ERRORS: Record<string, string> = {
  no_cash_payment: 'No cash was paid on this order, so it has no GST invoice',
  gst_invoice_not_generated:
    'The GST invoice for this order was never generated',
};

// Admin download of an order's GST or Full invoice PDF. With a blob response the
// error body arrives as a Blob too, so it is read back as JSON before showing it.
export const downloadOrderInvoice = createAsyncThunk(
  'store/downloadOrderInvoice',
  async (
    {
      transactionId,
      kind,
    }: { transactionId: string; kind: PaymentApiGetAdminOrderInvoiceKindEnum },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetAdminOrderInvoice(
        { transactionId, kind },
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(
        new Blob([res.data as unknown as BlobPart], {
          type: 'application/pdf',
        }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${kind}_invoice_${transactionId.slice(0, 8)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      return true;
    } catch (err: any) {
      let body = err?.response?.data;
      if (body instanceof Blob) {
        try {
          body = JSON.parse(await body.text());
        } catch {
          body = undefined;
        }
      }
      enqueueSnackbar(
        INVOICE_ERRORS[body?.error] ?? getFormattedErr(body?.error),
        { variant: 'error' },
      );
      return rejectWithValue(body);
    }
  },
);

const omitIfBlank = (value?: string | null) =>
  value === null || value === undefined || value.trim() === ''
    ? undefined
    : value;

export const uploadOrderVoucher = createAsyncThunk(
  'store/uploadOrderVoucher',
  async (
    {
      transactionId,
      imageFile,
      description,
      giftCardCode,
      pin,
      expiresAt,
      redemptionLink,
      removeImage,
    }: {
      transactionId: string;
      imageFile: File | null;
      description: string;
      giftCardCode?: string | null;
      pin?: string | null;
      expiresAt?: string | null;
      redemptionLink?: string | null;
      removeImage?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      // The generated client appends every field that isn't `undefined`, and
      // FormData stringifies whatever it gets — so a `null` here is posted as
      // the literal "null". Omit blanks instead of sending them.
      await (
        await apiClient.getPaymentApi()
      ).paymentApiUploadOrderVoucher({
        transactionId,
        imageFile: imageFile ?? undefined,
        description,
        giftCardCode: omitIfBlank(giftCardCode),
        pin: omitIfBlank(pin),
        expiresAt: omitIfBlank(expiresAt),
        redemptionLink: omitIfBlank(redemptionLink),
        removeImage: removeImage || undefined,
      });
      enqueueSnackbar('Voucher uploaded successfully', { variant: 'success' });
      return true;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postRareStoreItemsResponse = createAsyncThunk(
  'store/item/rare/response',
  async (userOfferResponseBody: UserOfferResponseBody, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiUserOfferItemResponse({ userOfferResponseBody });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getMySubscription = createAsyncThunk(
  'store/getMySubscription',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetMySubscription();
      // `null` is a real answer here ("no mandate"), so it must survive as null
      // rather than being coerced to undefined — the manage-plan surfaces use
      // undefined to mean "not asked yet".
      return {
        subscription: res.data.subscription ?? null,
        cancelNotice: res.data.cancel_notice ?? null,
      };
    } catch (err: any) {
      // Deliberately silent: this runs on mount of several surfaces and a
      // failure only means we cannot show the plan panel, which is not
      // something to interrupt the page with.
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getSubscriptionInvoices = createAsyncThunk(
  'store/getSubscriptionInvoices',
  async (subscriptionId: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetSubscriptionInvoices({ subscriptionId });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const cancelSubscription = createAsyncThunk(
  'store/cancelSubscription',
  async (
    param: {
      subscriptionId: string;
      cancelAtCycleEnd?: boolean;
      reason?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiCancelMySubscription({
        subscriptionId: param.subscriptionId,
        cancelSubscriptionBody: {
          // Defaults to end-of-cycle so the buyer keeps the period they paid
          // for; matches the backend default.
          cancel_at_cycle_end: param.cancelAtCycleEnd ?? true,
          reason: param.reason,
        },
      });
      const subscription = res.data.subscription ?? null;
      // Said from what the backend did, not what was asked: a mandate still in
      // its free month is cancelled outright, and its Pro ends with it.
      const trialTakenBack = !!subscription?.trial_access_ended_at;
      enqueueSnackbar(
        trialTakenBack
          ? 'Subscription cancelled. Nothing was charged, and your free Pro access and its AI credits have ended.'
          : param.cancelAtCycleEnd === false ||
              subscription?.status === 'cancelled'
            ? 'Subscription cancelled'
            : 'Subscription will not renew. You keep access until the end of your current period.',
        { variant: 'success', autoHideDuration: 6000 },
      );
      return subscription;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

/** "Yes, I cancelled it" — the buyer meant the cancel made from their UPI app. */
export const confirmSubscriptionCancel = createAsyncThunk(
  'store/confirmSubscriptionCancel',
  async (subscriptionId: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiConfirmExternalSubscriptionCancel({ subscriptionId });
      return res.data.cancel_notice ?? null;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

/**
 * "No, it was a mistake" — a new mandate carrying the cancelled one on. The
 * buyer still has to approve it in Razorpay's sheet before anything changes.
 */
export const restoreSubscription = createAsyncThunk(
  'store/restoreSubscription',
  async (subscriptionId: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiRestoreMySubscription({ subscriptionId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

/** The buyer closed the approval sheet of a restore without approving it. */
export const abandonSubscriptionRestore = createAsyncThunk(
  'store/abandonSubscriptionRestore',
  async (subscriptionRowId: string, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getPaymentApi()
      ).paymentApiAbandonSubscriptionRestore({
        subscriptionId: subscriptionRowId,
      });
      return res.data.cancel_notice ?? null;
    } catch (err: any) {
      // Silent: the unapproved mandate expires on its own, and the next
      // restore supersedes it.
      return rejectWithValue(err?.response?.data);
    }
  },
);
